import React from 'react';
/**
 * Sistema de clustering usando @changey/react-leaflet-markercluster profesional
 * Basado en Leaflet.markercluster - implementa comportamiento 'spiderfy' automáticamente
 * Los marcadores se distribuyen en círculo alrededor del cluster al hacer click
 * Incluye soporte para polilinea y numeración secuencial
 */
import { Marker, Popup, Polyline, useMap } from 'react-leaflet';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import L from 'leaflet';

// Importar estilos CSS para clustering
import './MarkerCluster.css';

// Iconos SVG importados desde assets
import motorOffIcon from '../../../../assets/img/alertas/ic-motor-off.svg';
import motorOnIcon from '../../../../assets/img/alertas/ic-motor-on.svg';

// Función para detectar si un evento tiene problemas
const esEventoConProblema = (evento) => {
  // Si ya viene calculado, usarlo (modo individual y general procesado)
  if (evento.hasOwnProperty('tieneProblema')) {
    return evento.tieneProblema;
  }
  // Si no, calcularlo (fallback)
  return evento.doble_encendido || evento.doble_apagado || 
         evento.diferencia === 1 || evento.diferencia > 1;
};

// Crear icono personalizado usando los SVGs reales con numeración opcional e indicador de problemas
const createMotorIcon = (tipo, isSelected = false, numero = null, tieneProblema = false) => {
  const size = isSelected ? 32 : 28;
  const borderWidth = isSelected ? 4 : 3;
  const color = tipo === 'apagado' ? '#dc3545' : '#28a745'; // Rojo para apagado, verde para encendido
  const borderColor = tieneProblema ? '#ff6b00' : color; // Borde naranja si tiene problema
  const iconSrc = tipo === 'apagado' ? motorOffIcon : motorOnIcon;
  
  // Efectos especiales para problemas
  const problemaEffects = tieneProblema ? `
    animation: ${isSelected ? 'pulse-problema' : 'blink-problema'} 2s infinite;
    box-shadow: 0 0 15px rgba(255, 107, 0, 0.8) !important;
  ` : '';
  
  const iconHtml = `
    <div style="
      background: white;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: ${borderWidth}px solid ${borderColor};
      box-shadow: 0 3px 8px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      ${isSelected ? 'animation: pulse 1.5s infinite; border-color: #ffc107; box-shadow: 0 4px 12px rgba(255, 193, 7, 0.6);' : ''}
      ${problemaEffects}
    ">
      <img src="${iconSrc}" 
           alt="${tipo === 'apagado' ? 'Motor apagado' : 'Motor encendido'}" 
           style="width: 16px; height: 16px; filter: ${tipo === 'apagado' ? 'brightness(0) saturate(100%) invert(23%) sepia(84%) saturate(2851%) hue-rotate(337deg) brightness(93%) contrast(95%)' : 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)'};" />
      ${tieneProblema ? `
        <div style="
          position: absolute;
          top: -5px;
          right: -5px;
          background: #ff6b00;
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          animation: rotate 2s linear infinite;
        ">⚠</div>
      ` : ''}
      ${numero ? `
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          background: #333;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">${numero}</div>
      ` : ''}
    </div>
  `;
  
  return L.divIcon({
    html: iconHtml,
    className: 'custom-motor-icon',
    iconSize: [size, size + (numero ? 10 : 0)], // Ajustar altura si hay número
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

// Crear icono de cluster con indicador de problemas
const createClusterIcon = (count, hasSelected = false, hasProblemas = false) => {
  const size = 40;
  //let color = '#007bff'; // Azul por defecto
  
//   if (hasProblemas) {
//     color = '#ff6b00'; // Naranja si hay problemas
//   } else if (hasSelected) {
//     color = '#ffc107'; // Amarillo si hay seleccionado
//   }

  const color = hasProblemas
      ? '#ff6b00'      // naranja  ← 1º si hay problemas
      : hasSelected
          ? '#ffc107'  // amarillo ← 2º si contiene el evento seleccionado
          : '#007bff'; // azul     ← 3º normal
  
  const clusterHtml = `
    <div style="
      background: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 4px 10px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 14px;
      font-family: Arial, sans-serif;
      position: relative;
      ${hasSelected ? 'animation: pulse 1.5s infinite;' : ''}
      ${hasProblemas ? 'animation: blink-problema 2s infinite;' : ''}
    ">
      ${count}
      ${hasProblemas ? `
        <div style="
          position: absolute;
          top: -3px;
          right: -3px;
          background: #dc3545;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          animation: rotate 1.5s linear infinite;
        ">⚠</div>
      ` : ''}
    </div>
  `;
  
  return L.divIcon({
    html: clusterHtml,
    className: 'custom-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

/**
 * Componente principal que renderiza los markers con clustering profesional
 * Incluye comportamiento spiderfy automático y soporte para polilinea secuencial
 */
export const MarkerClusterComponent = ({ eventos, selectedEvent, vehiculo, showPolyline = false, showNumbers = false }) => {
  console.log(`MarkerCluster: Renderizando ${eventos.length} eventos con clustering profesional`);
  
  // Filtrar eventos con coordenadas válidas
  const eventosValidos = eventos.filter(evento => {
    const lat = parseFloat(evento.latitud);
    const lng = parseFloat(evento.longitud);
    return lat && lng && 
          !isNaN(lat) && !isNaN(lng) &&
          lat >= -90 && lat <= 90 &&
          lng >= -180 && lng <= 180 &&
          lat !== 0 && lng !== 0;
  });

  // Ordenar eventos por fecha para la polilinea y numeración
  const eventosOrdenados = [...eventosValidos].sort((a, b) => 
    new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
  );

  // Crear puntos para la polilinea
  const puntosPolilinea = eventosOrdenados.map(evento => [
    parseFloat(evento.latitud),
    parseFloat(evento.longitud)
  ]);

  return (
    <>
      {/* Polilinea conectando los puntos en orden cronológico con efecto direccional animado */}
      {showPolyline && eventosOrdenados.length > 1 && (
        <>
          {/* Línea principal con animación direccional mejorada */}
          <Polyline
            positions={puntosPolilinea}
            className="animated-polyline" // Clase CSS para animación direccional
            pathOptions={{
              color: '#007bff',
              weight: 2,             // Línea más fina
              opacity: 0.8,
              dashArray: '10, 5',
              lineCap: 'round',
              lineJoin: 'round'
            }}
          />
          
          {/* Pequeña punta de flecha solo al final para indicar dirección final */}
          {(() => {
            const ultimoPunto = puntosPolilinea[puntosPolilinea.length - 1];
            const penultimoPunto = puntosPolilinea[puntosPolilinea.length - 2];
            
            if (!ultimoPunto || !penultimoPunto) return null;
            
            // Calcular vector de dirección del último segmento
            const dx = ultimoPunto[1] - penultimoPunto[1];
            const dy = ultimoPunto[0] - penultimoPunto[0];
            const longitud = Math.sqrt(dx * dx + dy * dy);
            
            if (longitud === 0) return null;
            
            const ux = dx / longitud;
            const uy = dy / longitud;
            
            // Crear pequeña punta de flecha sutil al final
            const tamaño = 0.0001; // Muy sutil
            const puntaFinal = [
              ultimoPunto,
              [ultimoPunto[0] - ux * tamaño - uy * tamaño * 0.5, ultimoPunto[1] - uy * tamaño + ux * tamaño * 0.5],
              [ultimoPunto[0] - ux * tamaño + uy * tamaño * 0.5, ultimoPunto[1] - uy * tamaño - ux * tamaño * 0.5]
            ];
            
            return (
              <Polyline
                key="final-arrow"
                positions={puntaFinal}
                pathOptions={{
                  color: '#0056b3',
                  weight: 3,
                  opacity: 0.9,
                  lineCap: 'round',
                  lineJoin: 'round'
                }}
              />
            );
          })()}
        </>
      )}

      {/* Componente interno para clustering dinámico */}
      <DynamicClusterGroup 
        eventos={eventosOrdenados}
        selectedEvent={selectedEvent}
        vehiculo={vehiculo}
        showNumbers={showNumbers}
      />
    </>
  );
};

// Componente interno que maneja clustering con radio dinámico y spiderfy mejorado
const DynamicClusterGroup = ({ eventos, selectedEvent, vehiculo, showNumbers }) => {
  const map = useMap();
  
  // Calcular radio dinámico basado en zoom actual
  const currentZoom = map.getZoom();
  let maxRadius;
  
  if (currentZoom >= 16) maxRadius = 3;   // Muy cerca - mínima agrupación
  else if (currentZoom >= 14) maxRadius = 6;   // Cerca - poca agrupación  
  else if (currentZoom >= 12) maxRadius = 10;  // Normal - agrupación moderada
  else if (currentZoom >= 10) maxRadius = 15;  // Lejos - más agrupación
  else maxRadius = 20;                         // Muy lejos - máxima agrupación

  console.log(`DynamicClusterGroup: Zoom=${currentZoom}, Radio=${maxRadius}`);

  return (
    <>
      {/* Render problem markers standalone so they are always visible in general view */}
      <MarkerClusterGroup
        key={`cluster-${currentZoom}`} // Forzar re-render al cambiar zoom
          iconCreateFunction={cluster => {
            const childs = cluster.getAllChildMarkers();
            
            // 🎯 SOLUCIÓN SIMPLE Y FIABLE: Leer flags directos del L.Marker
            const hasProblemas = childs.some(marker => marker.tieneProblema);
            const hasSelected = childs.some(marker => marker.isSelected);

            console.log('🎯 CLUSTER-FINAL', map.getZoom(), cluster.getChildCount(),
                        'problemas:', hasProblemas);

            return createClusterIcon(cluster.getChildCount(), hasSelected, hasProblemas);
        }}

        eventHandlers={{
            clusterclick: e => {
            const childMarkers = e.layer.getAllChildMarkers();
            
            // 🎯 BUSCAR PROBLEMAS DIRECTAMENTE EN EL L.MARKER
            const problem = childMarkers.find(marker => marker.tieneProblema);

            if (problem) {
                map.setView(problem.getLatLng(),
                            Math.min(map.getZoom() + 2, 18));
                // setTimeout(() => problem.openPopup(), 250); // POPUP DEL CLUSTER DESHABILITADO - Solo se centra sin mostrar popup
            } else {
                e.layer.zoomToBounds();
            }
            }
        }}
        options={{
    maxClusterRadius: maxRadius,
    disableClusteringAtZoom: 18,
    spiderfyOnMaxZoom: true,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: false,
    spiderfyDistanceMultiplier: 1.2,
    removeOutsideVisibleBounds: true,
    animate: true,
    chunkedLoading: true
  }}
        
    //     options={{
    //       maxClusterRadius: maxRadius, // Radio dinámico basado en zoom
    //       disableClusteringAtZoom: 18, // Solo desactivar en zoom máximo
    //       spiderfyOnMaxZoom: true, // Spiderfy automático en zoom máximo
    //       showCoverageOnHover: false, // No mostrar área de cobertura
    //       zoomToBoundsOnClick: false, // Desactivar default zoom
    //       spiderfyDistanceMultiplier: 1.2, // Espaciado para spiderfy
    //       removeOutsideVisibleBounds: true, // Optimización
    //       animate: true, // Animaciones suaves
    //       chunkedLoading: true, // Carga por chunks
    //       iconCreateFunction: (cluster) => {
    //         const count = cluster.getChildCount();
    //         const childMarkers = cluster.getAllChildMarkers();
    //          childMarkers.forEach(m => {
    //         /* mostraremos id, zoom actual y los 3 lugares posibles del flag */
    //         console.log(
    //         'CHK',
    //         map.getZoom(),
    //         m._leaflet_id,
    //         'opt:',        m.options?.tieneProblema,
    //         'eventData:',  m.options?.eventData?.tieneProblema,
    //         'directProp:', m.tieneProblema          // <- comprobaremos si esto existe
    //         );
    //     });
            
    //         // Verificar si hay marcadores seleccionados - solo desde options
    //         const hasSelected = childMarkers.some(marker => marker.options?.isSelected);
            
    //         // Verificar si hay marcadores con problemas - solo desde options
    //         //const hasProblemas = childMarkers.some(marker => marker.options?.tieneProblema);
            
    // const hasProblemas = childMarkers.some(m => m.options?.tieneProblema ||
    //                                         m.options?.eventData?.tieneProblema ||
    //                                         m.tieneProblema);
            
    //         console.log(`🎯 Cluster con ${count} marcadores - hasProblemas: ${hasProblemas}, hasSelected: ${hasSelected}`);
            
    //         return createClusterIcon(count, hasSelected, hasProblemas);
    //       }
    //     }}
    //     // Al hacer click en un cluster: si contiene un marcador con problema, centrar y mostrarlo; sino zoom a bounds
    //     onClusterClick={(e) => {
    //       const cluster = e.layer;
    //       const childMarkers = cluster.getAllChildMarkers();
          
    //       // Buscar marcadores con problemas - solo desde options
    //       const problemMarker = childMarkers.find(marker => marker.options?.tieneProblema);
          
    //       if (problemMarker) {
    //         console.log('🎯 Cluster con problemas clickeado - centrando en marcador problemático');
    //         const pos = problemMarker.getLatLng();
    //         map.setView(pos, map.getZoom() + 2);
    //         problemMarker.openPopup();
    //       } else {
    //         console.log('🔍 Cluster normal clickeado - haciendo zoom a bounds');
    //         cluster.zoomToBounds();
    //       }
    //     }}
      >
      {eventos.map((evento, index) => {
        const lat = parseFloat(evento.latitud);
        const lng = parseFloat(evento.longitud);
        const isSelected = selectedEvent && selectedEvent.fecha === evento.fecha;
        const numeroSecuencia = showNumbers ? index + 1 : null;
        const tieneProblema = esEventoConProblema(evento); // Usar función de detección
        const tipoEventoNormalizado = evento.tipoEvento === 'apagado' ? 'apagado' : 'encendido';
        
        // 🔍 LOG CRÍTICO: Verificar si detectamos problemas
        if (tieneProblema) {
          console.log('🚨 EVENTO CON PROBLEMA DETECTADO:', {
            index,
            fecha: evento.fecha,
            tipoEvento: evento.tipoEvento,
            nombreEvento: evento.nombreEvento,
            doble_encendido: evento.doble_encendido,
            doble_apagado: evento.doble_apagado,
            diferencia: evento.diferencia,
            tieneProblema: tieneProblema
          });
        }
        
        const icon = createMotorIcon(tipoEventoNormalizado, isSelected, numeroSecuencia, tieneProblema);
        const iconColor = evento.tipoEvento === 'apagado' ? '#dc3545' : '#28a745';
        
        return (
          <Marker
            key={`${evento.fecha}-${index}`}
            position={[lat, lng]}
            icon={icon}
            ref={marker => {
              if (marker) {
                // 🎯 SOLUCIÓN DEFINITIVA: Asignar flags directamente al objeto L.Marker
                marker.tieneProblema = tieneProblema;
                marker.isSelected = isSelected;
              }
            }}
            zIndexOffset={isSelected ? 1000 : 0}
          >
            <Popup>
              <div style={{ minWidth: '200px' }}>
                <h6 className="mb-2">
                  <i className={`fas ${evento.tipoEvento === 'apagado' ? 'fa-stop-circle' : 'fa-play-circle'} me-2`}
                     style={{ color: iconColor }}></i>
                  {showNumbers && <span className="badge bg-secondary me-2">#{numeroSecuencia}</span>}
                  {evento.nombreEvento}
                </h6>
                
                {vehiculo && (
                  <p className="mb-1">
                    <strong>Vehículo:</strong> {vehiculo.alias || evento.alias}
                  </p>
                )}
                
                <p className="mb-1">
                  <strong>Fecha:</strong><br/>
                  {new Date(evento.fecha).toLocaleString('es-MX')}
                </p>
                
                {showNumbers && (
                  <p className="mb-1">
                    <strong>Secuencia:</strong> Evento #{numeroSecuencia} de {eventos.length}
                  </p>
                )}
                
                <p className="mb-1">
                  <strong>Coordenadas:</strong><br/>
                  {lat.toFixed(6)}, {lng.toFixed(6)}
                </p>
                
                {evento.velocidad !== undefined && (
                  <p className="mb-1">
                    <strong>Velocidad:</strong> {evento.velocidad} km/h
                  </p>
                )}
                
                {evento.distancia !== undefined && (
                  <p className="mb-1">
                    <strong>Distancia:</strong> {(evento.distancia / 1000).toFixed(2)} km
                  </p>
                )}
                
                {tieneProblema && (
                  <div className="alert alert-warning alert-sm mt-2 mb-0">
                    <small>
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      {evento.tipoProblema || 'Doble encendido/apagado detectado'}
                    </small>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MarkerClusterGroup>
    </>
  );
};
